import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { api } from '../api/client.js';

// Towns, amenities, property types and platform details, fetched once. Every
// dropdown in the app reads from here so nothing drifts out of step with the API.

const MetaContext = createContext(null);

const FALLBACK = {
  currency: { code: 'XAF', symbol: 'FCFA' },
  cities: [],
  regions: [],
  propertyTypes: [],
  amenities: [],
  documentKinds: [],
  reportReasons: [],
  billingPeriods: [],
  chargeKinds: [],
  paymentMethods: [],
  momoProviders: [],
  utilityKinds: [],
  utilityBillingModes: [],
  utilityAccountStatuses: [],
  managerCapabilities: [],
  landTitles: [],
  landUses: [],
  buyerFinance: [],
  platform: { name: 'Cameroon Homes' }
};

export const MetaProvider = ({ children }) => {
  const [meta, setMeta] = useState(FALLBACK);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get('/meta')
      .then((data) => {
        if (!cancelled) setMeta({ ...FALLBACK, ...data });
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(() => {
    const amenityLabels = new Map(meta.amenities.map((item) => [item.slug, item.label]));
    const typeLabels = new Map(meta.propertyTypes.map((item) => [item.slug, item.label]));
    const cityByName = new Map(meta.cities.map((city) => [city.name, city]));
    const utilityLabels = new Map(meta.utilityKinds.map((item) => [item.slug, item.label]));

    return {
      ...meta,
      ready,
      amenityLabel: (slug) => amenityLabels.get(slug) || slug.replace(/_/g, ' '),
      propertyTypeLabel: (slug) => typeLabels.get(slug) || slug?.replace(/_/g, ' ') || '',
      city: (name) => cityByName.get(name),
      utilityKindLabel: (slug) => utilityLabels.get(slug) || slug?.replace(/_/g, ' ') || '',
      landTitleLabel: (slug) => meta.landTitles.find((item) => item.slug === slug)?.label || '',
      landUseLabel: (slug) => meta.landUses.find((item) => item.slug === slug)?.label || '',
      financeLabel: (slug) => meta.buyerFinance.find((item) => item.slug === slug)?.label || slug || ''
    };
  }, [meta, ready]);

  return <MetaContext.Provider value={value}>{children}</MetaContext.Provider>;
};

export const useMeta = () => {
  const context = useContext(MetaContext);
  if (!context) throw new Error('useMeta must be used inside MetaProvider');
  return context;
};
